import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { STORAGE_KEYS } from '@/constants';
import { listMyMappedDistributors } from '@/services/salesTeamMapping';
import { getMyCompanyUsers } from '@/services/userCompaniesService';
import managerIcon from '@/assets/ sales/Icon (3).svg';
import companyIcon from '@/assets/ sales/Icon (4).svg';
import distributorIcon from '@/assets/ sales/Icon (5).svg';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import './SalesProfilePopover.css';

function getInitials(name = '') {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function extractUserData(raw) {
  if (!raw) return null;
  try {
    const u = JSON.parse(raw);
    const user = u.user || u;
    return {
      id: u.id || user.id,
      user_name: user.user_name || user.username || user.name || u.user_name || u.username || u.name || 'User',
      role_name: u.companyRole?.role_name || u.role_name || u.company_role?.role_name || '',
      manager_name: u.manager?.user_name || u.manager?.username || u.managers?.[0]?.user_name || '-',
      company_name: u.company?.company_name || u.company_name || '-',
    };
  } catch {
    return null;
  }
}

const SalesProfilePopover = ({ onClose }) => {
  const navigate = useNavigate();
  const popoverRef = useRef(null);

  const [userData, setUserData] = useState(null);
  const [companyDetail, setCompanyDetail] = useState(null);
  const [totalDistributors, setTotalDistributors] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    setUserData(extractUserData(raw));
  }, []);

  useEffect(() => {
    if (!userData?.id) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const listRes = await getMyCompanyUsers({ page: 1, pageSize: 100 });
        const items = listRes?.data?.items || listRes?.items || listRes?.data || [];
        const match = Array.isArray(items)
          ? items.find(r => r.user_id === userData.id || r.user?.id === userData.id)
          : null;

        if (match) {
          setCompanyDetail(match);
        }
        const distRes = await listMyMappedDistributors();
        const distributors = distRes?.data?.distributors || distRes?.distributors || [];
        setTotalDistributors(distributors.length);
      } catch {
        console.error("error")
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userData?.id]);

  useEffect(() => {
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handler);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [onClose]);

  const userName = userData?.user_name || 'User';
  const roleName = companyDetail?.companyRole?.role_name || companyDetail?.company_role?.role_name || userData?.role_name || 'Sales Representative';
  const managerName = companyDetail?.manager?.user_name || companyDetail?.manager?.username || userData?.manager_name || '-';
  const companyName = companyDetail?.company?.company_name || userData?.company_name || '-';

  return (
    <div className="spp-popover" ref={popoverRef}>
      <button className="spp-close" onClick={onClose} aria-label="Close">×</button>

      {loading ? (
        <div className="spp-loading">
          <LoadingSpinner size="md" />
        </div>
      ) : (
        <>
          <div className="spp-header">
            <div className="spp-avatar">{getInitials(userName)}</div>
            <div className="spp-header-info">
              <span className="spp-name">{userName}</span>
              <span className="spp-role">{roleName.toUpperCase()}</span>
            </div>
          </div>

          <div className="spp-details">
            <div className="spp-detail-row">
              <div className="spp-detail-icon-wrap">
                <img src={managerIcon} alt="" width="18" height="18" />
              </div>
              <div>
                <p className="spp-detail-label">MANAGER</p>
                <p className="spp-detail-value">{managerName}</p>
              </div>
            </div>

            <div className="spp-detail-row">
              <div className="spp-detail-icon-wrap">
                <img src={companyIcon} alt="" width="18" height="18" />
              </div>
              <div>
                <p className="spp-detail-label">COMPANY</p>
                <p className="spp-detail-value">{companyName}</p>
              </div>
            </div>

            <div className="spp-detail-row">
              <div className="spp-detail-icon-wrap">
                <img src={distributorIcon} alt="" width="18" height="18" />
              </div>
              <div>
                <p className="spp-detail-label">TOTAL ASSIGNED DISTRIBUTOR</p>
                <p className="spp-detail-value spp-detail-value--large">{totalDistributors}</p>
              </div>
            </div>
          </div>

          <button
            className="spp-view-btn"
            onClick={() => { navigate('/sales-profile'); onClose?.(); }}
          >
            View Profile
          </button>
        </>
      )}

      <div className="spp-arrow" />
    </div>
  );
};

export default SalesProfilePopover;
